import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Animated,
  Easing,
  Pressable,
  TextInput,
  TextInputContentSizeChangeEvent,
  View,
  useWindowDimensions,
  StyleProp,
  ViewStyle,
} from 'react-native'
import { useFocusEffect } from '@react-navigation/native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { IconButton } from '@components/base'
import { useVoice } from '@utils/voice'
import { useNoraStreamChat } from '@screens/Nora/context/NoraStreamChatContext.tsx'
import { GradientBorder } from '@components/GradientBorder'
import { LinearGradient } from '@src/components/LinearGradient'
import { NoraVoiceModeSource } from '@src/screens/Nora/constants'

const SILENCE_SUBMIT_DELAY_MS = 2000
const HORIZONTAL_MARGIN = 20
const FAB_SIZE = 64
const MIC_PULSE_DURATION_MS = 1200
const MICROPHONE_BACKGROUND_COLOR = '#11494F'
const MICROPHONE_PULSE_COLOR = 'rgba(255, 255, 255, 0.24)'
const TRANSCRIPT_LINE_HEIGHT = 20
const MAX_TRANSCRIPT_LINES = 3
const TRANSCRIPT_VERTICAL_PADDING = 2
const TRANSCRIPT_TOP_FADE_MIN_LINES = 3

interface HomeActionButtonProps {
  onOpenMenu: () => void
  style?: StyleProp<ViewStyle>
}

export const HomeActionButton = ({ onOpenMenu, style }: HomeActionButtonProps) => {
  const styles = useStyleSheet(themedStyle)
  const { width: screenWidth } = useWindowDimensions()
  const { onAsk } = useNoraStreamChat()
  const [isVoiceCaptureActive, setIsVoiceCaptureActive] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [numberOfLines, setNumberOfLines] = useState(1)
  const theme = useTheme()

  const handleContentSizeChange = useCallback((e: TextInputContentSizeChangeEvent) => {
    const contentHeight = e.nativeEvent.contentSize.height - TRANSCRIPT_VERTICAL_PADDING * 2
    const lines = Math.max(
      1,
      Math.min(MAX_TRANSCRIPT_LINES, Math.ceil(contentHeight / TRANSCRIPT_LINE_HEIGHT)),
    )
    setNumberOfLines(lines)
  }, [])

  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isVoiceCaptureActiveRef = useRef(isVoiceCaptureActive)
  const isSubmittingRef = useRef(false)
  const longPressActivatedRef = useRef(false)
  // eslint-disable-next-line require-await
  const stopListeningRef = useRef<() => Promise<void>>(async () => undefined)
  const pulseAnimation = useRef(new Animated.Value(0)).current

  isVoiceCaptureActiveRef.current = isVoiceCaptureActive

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current)
      silenceTimerRef.current = null
    }
  }, [])

  const stopListeningSafely = useCallback(async () => {
    try {
      await stopListeningRef.current()
    } catch {
      // Stopping voice capture is best-effort; local UI cleanup should still continue.
    }
  }, [])

  const submitTranscript = useCallback(
    async (transcriptToSubmit: string) => {
      const prompt = transcriptToSubmit.trim()

      if (!prompt || isSubmittingRef.current) {
        return
      }

      isSubmittingRef.current = true
      clearSilenceTimer()
      try {
        await stopListeningSafely()
        await onAsk({
          prompt,
          initialVoiceModeEnabled: true,
          initialVoiceModeSource: NoraVoiceModeSource.HomeActionButtonLongPress,
        })
        setTranscript('')
        setNumberOfLines(1)
        setIsVoiceCaptureActive(false)
        longPressActivatedRef.current = false
      } finally {
        isSubmittingRef.current = false
      }
    },
    [clearSilenceTimer, onAsk, stopListeningSafely],
  )

  const scheduleSilenceSubmit = useCallback(
    (transcriptToSubmit: string) => {
      clearSilenceTimer()
      silenceTimerRef.current = setTimeout(() => {
        void submitTranscript(transcriptToSubmit).catch(() => undefined)
      }, SILENCE_SUBMIT_DELAY_MS)
    },
    [clearSilenceTimer, submitTranscript],
  )

  const handleTranscript = useCallback(
    (newText: string) => {
      setTranscript((currentTranscript) => {
        const nextTranscript = currentTranscript ? `${currentTranscript} ${newText}` : newText
        scheduleSilenceSubmit(nextTranscript)
        return nextTranscript
      })
    },
    [scheduleSilenceSubmit],
  )

  const { isAvailable, startListening, stopListening } = useVoice(handleTranscript)
  stopListeningRef.current = stopListening

  const cancelVoiceCapture = useCallback(async () => {
    clearSilenceTimer()
    try {
      await stopListeningSafely()
    } finally {
      setTranscript('')
      setNumberOfLines(1)
      setIsVoiceCaptureActive(false)
      longPressActivatedRef.current = false
    }
  }, [clearSilenceTimer, stopListeningSafely])

  useEffect(() => {
    return () => {
      clearSilenceTimer()
      void stopListeningSafely()
    }
  }, [clearSilenceTimer, stopListeningSafely])

  useFocusEffect(
    useCallback(() => {
      return () => {
        if (isVoiceCaptureActiveRef.current) {
          void cancelVoiceCapture().catch(() => undefined)
        }
      }
    }, [cancelVoiceCapture]),
  )

  useEffect(() => {
    if (!isVoiceCaptureActive) {
      pulseAnimation.setValue(0)
      return
    }

    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnimation, {
          toValue: 1,
          duration: MIC_PULSE_DURATION_MS,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnimation, {
          toValue: 0,
          duration: 0,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
      ]),
    )

    pulse.start()

    return () => pulse.stop()
  }, [isVoiceCaptureActive, pulseAnimation])

  const handleLongPress = useCallback(async () => {
    if (!isAvailable || isVoiceCaptureActive) {
      return
    }

    longPressActivatedRef.current = true
    setIsVoiceCaptureActive(true)
    await startListening()
  }, [isAvailable, isVoiceCaptureActive, startListening])

  const handleOpenMenu = useCallback(() => {
    if (longPressActivatedRef.current || isVoiceCaptureActive) {
      return
    }

    onOpenMenu()
  }, [isVoiceCaptureActive, onOpenMenu])

  if (!isVoiceCaptureActive) {
    return (
      <IconButton
        accessibilityLabel="Open Menu"
        iconName="plus"
        size="l"
        style={style}
        type="primary"
        onLongPress={handleLongPress}
        onPress={handleOpenMenu}
      />
    )
  }

  const captureWidth = Math.max(0, screenWidth - HORIZONTAL_MARGIN * 2)
  const showTranscriptTopFade = numberOfLines >= TRANSCRIPT_TOP_FADE_MIN_LINES
  const transcriptSurfaceColor = theme['theme.background']
  const pulseStyles = {
    opacity: pulseAnimation.interpolate({
      inputRange: [0, 1],
      outputRange: [0.5, 0],
    }),
    transform: [
      {
        scale: pulseAnimation.interpolate({
          inputRange: [0.3, 1],
          outputRange: [1, 1.34],
        }),
      },
    ],
  }

  return (
    <>
      <Pressable
        accessible={false}
        testID="HomeActionButton/DismissBackdrop"
        style={styles.dismissBackdrop}
        onPress={cancelVoiceCapture}
      />
      <View
        testID="HomeActionButton/GradientInput"
        style={[styles.inputContainer, { width: captureWidth }, style]}
      >
        <GradientBorder
          borderWidth={2}
          borderRadius={16}
          style={{ borderTopRightRadius: 999, borderBottomRightRadius: 999 }}
        >
          <View style={styles.innerInputWrapper}>
            <TextInput
              accessibilityLabel="Nora voice prompt"
              editable={false}
              multiline
              onContentSizeChange={handleContentSizeChange}
              pointerEvents="none"
              scrollEnabled
              value={transcript}
              selection={{ start: transcript.length, end: transcript.length }}
              style={styles.transcriptInput}
            />
            {showTranscriptTopFade && (
              <View
                pointerEvents="none"
                style={styles.transcriptTopFade}
                testID="HomeActionButton/TranscriptTopFade"
              >
                <LinearGradient
                  start={{ x: 0, y: 0 }}
                  end={{ x: 0, y: 1 }}
                  startColor={transcriptSurfaceColor}
                  endColor={transcriptSurfaceColor}
                  startOpacity={1}
                  endOpacity={0.3}
                  startOffset={0}
                  endOffset={1}
                  style={styles.transcriptTopFadeGradient}
                />
              </View>
            )}
          </View>
        </GradientBorder>
      </View>
      <View style={[styles.microphoneContainer, style]}>
        <View
          testID="HomeActionButton/MicrophoneButton"
          style={[styles.microphoneButtonShell, { backgroundColor: MICROPHONE_BACKGROUND_COLOR }]}
        >
          <Animated.View
            testID="HomeActionButton/MicrophonePulse"
            pointerEvents="none"
            style={[
              styles.microphonePulse,
              pulseStyles,
              { backgroundColor: MICROPHONE_PULSE_COLOR },
            ]}
          />
          <IconButton
            accessibilityLabel="Stop Nora voice prompt"
            iconName="microphone"
            size="l"
            type="transparent"
            style={styles.microphoneButton}
            iconProps={{ style: styles.microphoneIcon }}
            onPress={cancelVoiceCapture}
          />
        </View>
      </View>
    </>
  )
}

const themedStyle = StyleService.create({
  dismissBackdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  inputContainer: {
    position: 'absolute',
    right: HORIZONTAL_MARGIN,
    minHeight: FAB_SIZE,
    justifyContent: 'center',
    zIndex: 1,
  },
  innerInputWrapper: {
    minHeight: FAB_SIZE - 8,
    justifyContent: 'center',
    borderTopLeftRadius: 48,
    borderTopRightRadius: 999,
    borderBottomRightRadius: 999,
    borderBottomLeftRadius: 48,
    overflow: 'hidden',
    backgroundColor: 'theme.background',
  },
  transcriptInput: {
    maxHeight: TRANSCRIPT_LINE_HEIGHT * MAX_TRANSCRIPT_LINES + TRANSCRIPT_VERTICAL_PADDING * 2,
    paddingLeft: 16,
    paddingRight: FAB_SIZE + 8,
    paddingTop: TRANSCRIPT_VERTICAL_PADDING,
    paddingBottom: TRANSCRIPT_VERTICAL_PADDING,
    color: 'theme.text.primary',
    lineHeight: TRANSCRIPT_LINE_HEIGHT,
    textAlignVertical: 'center',
  },
  transcriptTopFade: {
    position: 'absolute',
    top: 0,
    left: 5,
    right: 0,
    height: TRANSCRIPT_LINE_HEIGHT * 2,
    zIndex: 1,
  },
  transcriptTopFadeGradient: {
    flex: 1,
  },
  microphoneContainer: {
    position: 'absolute',
    width: FAB_SIZE,
    height: FAB_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  microphoneButtonShell: {
    width: FAB_SIZE,
    height: FAB_SIZE,
    borderRadius: FAB_SIZE / 2,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  microphonePulse: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  microphoneButton: {
    zIndex: 1,
  },
  microphoneIcon: {
    color: 'theme.solid.white',
  },
})
